import { Check, ArrowRight, Clock } from "lucide-react";

const plans = [
  {
    name: "Acceso 90 días",
    duration: "90",
    unit: "días",
    description: "Ideal para avanzar con foco y recorrer la propuesta en un periodo definido.",
    features: [
      "Acceso a todos los módulos",
      "Clases grabadas",
      "Recursos de apoyo",
      "Herramientas básicas",
    ],
    featured: false,
  },
  {
    name: "Acceso 180 días",
    duration: "180",
    unit: "días",
    description: "La modalidad más equilibrada para completar la ruta con mayor continuidad.",
    features: [
      "Todo lo del acceso de 90 días",
      "Herramientas complementarias",
      "Acceso a la comunidad",
      "Mayor contexto de aprendizaje",
    ],
    featured: true,
    badge: "Recomendado",
  },
  {
    name: "Acceso 360 días",
    duration: "360",
    unit: "días",
    description: "Para quienes quieren avanzar con más amplitud de tiempo y referencia continua.",
    features: [
      "Todo lo del acceso de 180 días",
      "Actualizaciones incluidas",
      "Acceso extendido a recursos",
      "Mayor continuidad",
    ],
    featured: false,
  },
];

export default function PlansSection() {
  const scrollToForm = () => {
    document.getElementById("formulario")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="planes"
      data-testid="plans-section"
      className="py-20 md:py-28 bg-[#FAFAFA]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-sm uppercase tracking-[0.25em] font-semibold text-[#0F3D56] mb-4 block">
            Modalidades de acceso
          </span>
          <h2
            className="text-3xl md:text-5xl font-semibold text-[#111827] mb-6"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Elige tu tiempo de acceso
          </h2>
          <p className="text-base text-gray-600 leading-relaxed">
            Selecciona la modalidad que mejor se adapte a tu ritmo de aprendizaje. 
            Todas incluyen el contenido completo de la propuesta educativa provista por MINED.
          </p>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {plans.map((plan, index) => (
            <div
              key={index}
              data-testid={`plan-card-${index}`}
              className={`relative bg-white border ${
                plan.featured 
                  ? "border-[#0F3D56] shadow-xl scale-105" 
                  : "border-gray-200 hover:border-gray-300"
              } transition-all duration-300`}
            >
              {/* Badge */}
              {plan.badge && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="bg-[#0F3D56] text-white text-xs font-bold uppercase tracking-wider px-4 py-2">
                    {plan.badge}
                  </span>
                </div>
              )}

              <div className="p-8">
                {/* Plan Header */}
                <div className="text-center mb-8">
                  <h3 className="text-lg font-bold text-[#111827] mb-3">
                    {plan.name}
                  </h3>
                  <div className="flex items-baseline justify-center gap-1 mb-3">
                    <span className={`text-5xl font-bold ${plan.featured ? "text-[#0F3D56]" : "text-[#111827]"}`}>
                      {plan.duration}
                    </span>
                    <span className="text-lg text-gray-500">{plan.unit}</span>
                  </div>
                  <p className="text-sm text-gray-500">
                    {plan.description}
                  </p>
                </div>

                {/* Features */}
                <ul className="space-y-4 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start gap-3">
                      <div className={`flex-shrink-0 w-5 h-5 ${plan.featured ? "bg-[#0F3D56]" : "bg-gray-200"} flex items-center justify-center mt-0.5`}>
                        <Check size={12} className={plan.featured ? "text-white" : "text-gray-600"} />
                      </div>
                      <span className="text-sm text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <button
                  onClick={scrollToForm}
                  data-testid={`plan-cta-${index}`}
                  className={`w-full py-4 text-sm font-bold tracking-wide flex items-center justify-center gap-2 transition-all ${
                    plan.featured
                      ? "bg-[#0F3D56] text-white hover:bg-[#0A2A3C]"
                      : "bg-gray-100 text-[#111827] hover:bg-gray-200"
                  }`}
                >
                  Solicitar Información
                  <ArrowRight size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Note */}
        <div className="mt-12 text-center space-y-2">
          <div className="inline-flex items-center gap-2 text-sm text-gray-500">
            <Clock size={16} />
            <span>El acceso comienza desde el momento de tu inscripción.</span>
          </div>
          <p className="text-xs text-gray-400">
            Programa educativo provisto por MINED. Orientación comercial por DSA.
          </p>
        </div>
      </div>
    </section>
  );
}
